import { z } from "zod";

/**
 * Runtime configuration.
 *
 * Every vendor-specific or region-specific choice -- API origin, cloud
 * region, identity service, storage location, mail and anti-abuse providers
 * -- is selected here rather than hard-coded, so a China-region deployment is
 * a configuration plus adapter change, not a source fork.
 *
 * Note that `deploymentRegion` and the user's language are entirely separate.
 * Switching the international site to Chinese must never move the user to a
 * different backend.
 */
export const deploymentRegionSchema = z.enum(["international", "china"]);
export type DeploymentRegion = z.infer<typeof deploymentRegionSchema>;

export const videoLimitsSchema = z.object({
  /** Hard ceiling on a single recording, enforced in the API and in storage rules. */
  maxBytes: z
    .number()
    .int()
    .positive()
    .default(200 * 1024 * 1024),
  /** Recording stops at this length in the browser; also checked server-side. */
  maxDurationMs: z
    .number()
    .int()
    .positive()
    .default(3 * 60 * 1000),
  /** Per-user ceiling on live (non-deleted) recordings. */
  maxActiveVideosPerUser: z.number().int().positive().default(10),
  /**
   * Accepted container types. Deliberately broad because MediaRecorder
   * support differs by browser; the client reports what it actually produced.
   */
  allowedMimeTypes: z
    .array(z.string())
    .default([
      "video/webm",
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp9,opus",
      "video/mp4",
      "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
      "video/x-matroska",
      "video/quicktime",
    ]),
  /** Lifetime of an authenticated playback URL. */
  playbackUrlTtlSeconds: z.number().int().positive().default(600),
  /**
   * How long a recording is kept. Null means "no automatic deletion".
   * See docs/open-questions.md Q4 -- the business retention period is not yet
   * decided, so nothing is deleted automatically until it is.
   */
  retentionDays: z.number().int().positive().nullable().default(null),
});
export type VideoLimits = z.infer<typeof videoLimitsSchema>;

export const rateLimitRuleSchema = z.object({
  limit: z.number().int().positive(),
  windowSeconds: z.number().int().positive(),
});
export type RateLimitRule = z.infer<typeof rateLimitRuleSchema>;

export const rateLimitsSchema = z.object({
  contactFormPerIp: rateLimitRuleSchema.default({
    limit: 5,
    windowSeconds: 3600,
  }),
  contactFormPerEmail: rateLimitRuleSchema.default({
    limit: 3,
    windowSeconds: 3600,
  }),
  uploadIntentPerUser: rateLimitRuleSchema.default({
    limit: 20,
    windowSeconds: 3600,
  }),
  submissionCreatePerUser: rateLimitRuleSchema.default({
    limit: 20,
    windowSeconds: 3600,
  }),
  adminExportPerUser: rateLimitRuleSchema.default({
    limit: 10,
    windowSeconds: 3600,
  }),
});
export type RateLimits = z.infer<typeof rateLimitsSchema>;

export const appConfigSchema = z.object({
  /** Which deployment this process is. Never inferred from the user's language. */
  deploymentRegion: deploymentRegionSchema.default("international"),
  /** Cloud region for compute. Kept adjacent to data and media regions. */
  cloudRegion: z.string().min(1).default("us-central1"),
  /** Public origin of the site, used for canonical URLs and email links. */
  publicSiteOrigin: z.string().url(),
  /** Origins permitted to call the API. Exact matches only -- no wildcards. */
  allowedOrigins: z.array(z.string().url()).min(1),
  storageBucket: z.string().min(1),
  /** Salt for hashing request IPs. Must come from a secret, never source. */
  ipHashSalt: z.string().min(16),
  /**
   * One-time secret that authorises creation of the very first administrator.
   * There is no default administrator account.
   */
  adminBootstrapSecret: z.string().min(24).nullable().default(null),
  videoLimits: videoLimitsSchema.default({}),
  rateLimits: rateLimitsSchema.default({}),
  adminExportHardLimit: z.number().int().positive().default(5000),
  defaultPageSize: z.number().int().positive().max(100).default(20),
  maxPageSize: z.number().int().positive().max(200).default(100),
  /** Draft/idempotency key retention. */
  idempotencyTtlSeconds: z
    .number()
    .int()
    .positive()
    .default(24 * 3600),
});

export type AppConfig = z.infer<typeof appConfigSchema>;

export const parseAppConfig = (input: unknown): AppConfig =>
  appConfigSchema.parse(input);

/**
 * Subset of configuration that is safe to publish to the browser. Used by the
 * frontend so limits shown in the UI always match what the server enforces.
 */
export interface PublicRuntimeConfig {
  deploymentRegion: DeploymentRegion;
  authProvider: string;
  storageProvider: string;
  antiAbuseProvider: string;
  antiAbuseEnabled: boolean;
  video: {
    maxBytes: number;
    maxDurationMs: number;
    maxActiveVideosPerUser: number;
    allowedMimeTypes: string[];
  };
}
